import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type AuthContextJSON = z.infer<typeof AuthContextSchema.AuthContextJSONZ>;
export type AuthContextParams = z.infer<typeof AuthContextSchema.AuthContextZ>;

export default class AuthContextSchema {
  public static readonly AuthContextJSONZ = z.object({
    isVendor: z.boolean(),
    isClient: z.boolean(),
    viewingAsVendor: z.boolean().nullish(),
    isVendorRepresentative: z.boolean(),
    isClientRepresentative: z.boolean(),
    // timestamp: z.string()
  });

  public static readonly AuthContextZ = z.object({
    isVendor: z.boolean(),
    isClient: z.boolean(),
    viewingAsVendor: z.boolean().nullable(),
    isVendorRepresentative: z.boolean(),
    isClientRepresentative: z.boolean(),
    // timestamp: DateSchema.type
  });

  public static validate(values: unknown): AuthContextParams {
    return parseOrThrow(
      AuthContextSchema.AuthContextZ,
      values,
      { 
        logError: true,
        schemaName: "AuthContextSchema",
        operation: "validate",
        payloadSample: values 
      }
    );
  }

  public static validateJSON(json: unknown): AuthContextJSON {
    return parseOrThrow(
      AuthContextSchema.AuthContextJSONZ,
      json,
      { 
        logError: true,
        schemaName: "AuthContextSchema",
        operation: "validateJSON",
        payloadSample: json 
      }
    );
  }

  public static isValid(values: unknown): values is AuthContextParams {
    try {
      AuthContextSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AuthContextJSON {
    try {
      AuthContextSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AuthContextJSON): AuthContextParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(
      AuthContextSchema.AuthContextJSONZ,
      json,
      { 
        logError: true,
        schemaName: "AuthContextSchema",
        operation: "deserialize",
        payloadSample: json 
      }
    );
    return AuthContextSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: AuthContextParams): AuthContextJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AuthContextSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AuthContextSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AuthContextSchema.AuthContextJSONZ,
    AuthContextSchema.AuthContextZ, {
    decode: (data) => ({
      isClient: data.isClient,
      isVendor: data.isVendor,
      viewingAsVendor: data.viewingAsVendor ?? null,
      isVendorRepresentative: data.isVendorRepresentative,
      isClientRepresentative: data.isClientRepresentative,
      // timestamp: DateSchema.deserialize(data.timestamp)
    }),
    encode: (context) => ({
      isClient: context.isClient,
      isVendor: context.isVendor,
      viewingAsVendor: context.viewingAsVendor,
      isVendorRepresentative: context.isVendorRepresentative,
      isClientRepresentative: context.isClientRepresentative,
      // timestamp: DateSchema.serialize(context.timestamp)
    })
  });
}
